import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { s } from '../../strings';
import { IconX } from '../icons';
import { Button, type ButtonVariant } from './button';

/**
 * Modal built on the native <dialog> element: focus trapping, Esc to close and
 * inert background come for free. Becomes a bottom sheet on narrow screens.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const downOnBackdrop = useRef(false);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      try {
        d.showModal();
      } catch {
        d.setAttribute('open', '');
      }
    } else if (!open && d.open) d.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onMouseDown={(e) => {
        downOnBackdrop.current = e.target === ref.current;
      }}
      onClick={(e) => {
        if (e.target === ref.current && downOnBackdrop.current) onClose();
      }}
      className={cn(
        'm-auto max-h-[92dvh] w-[calc(100%-2rem)] overflow-hidden rounded-2xl bg-surface p-0 text-ink shadow-pop',
        'max-sm:mb-0 max-sm:w-full max-sm:max-w-none max-sm:rounded-b-none',
        size === 'sm' && 'max-w-md',
        size === 'md' && 'max-w-lg',
        size === 'lg' && 'max-w-3xl',
      )}
    >
      {open && (
        <div className="flex max-h-[92dvh] flex-col">
          <header className="flex items-start gap-3 border-b border-line px-5 pt-4 pb-3">
            <div className="flex-1 pt-1.5">
              <h2 id={titleId} className="text-lg font-semibold">
                {title}
              </h2>
              {description && <p className="mt-0.5 text-sm text-ink-muted">{description}</p>}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="-mr-2 flex size-11 items-center justify-center rounded-lg text-ink-muted hover:bg-ink/5 hover:text-ink"
              aria-label={s.common.close}
            >
              <IconX />
            </button>
          </header>
          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && (
            <footer className="flex flex-col-reverse gap-2 border-t border-line bg-canvas/60 px-5 py-3 sm:flex-row sm:justify-end">
              {footer}
            </footer>
          )}
        </div>
      )}
    </dialog>
  );
}

/** Promise-free confirm dialog; render conditionally or keep mounted with `open`. */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = s.common.areYouSure,
  body,
  confirmLabel = s.common.confirm,
  variant = 'danger',
  loading,
  children,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: ReactNode;
  body?: ReactNode;
  confirmLabel?: ReactNode;
  variant?: ButtonVariant;
  loading?: boolean;
  children?: ReactNode;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button onClick={onClose}>{s.common.cancel}</Button>
          <Button variant={variant} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {body && <p className="text-[15px] text-ink-muted">{body}</p>}
      {children}
    </Dialog>
  );
}

/** Tiny helper for "open a dialog with this item" state. */
export function useDialog<T = true>() {
  const [value, setValue] = useState<T | null>(null);
  return { value, open: (v: T) => setValue(v), close: () => setValue(null), isOpen: value !== null };
}
